'use client';

import HeadlineField from '@/components//customFields/HeadlineField';
import { HeadlineTag } from '@/components//customFields/types';
import { Button } from '@/components/button';
import HighlightCardComponent from '@/components/highlightCard/HighlightCard';
import { IconButton } from '@/components/iconButton';
import InlineTextLink from '@/components/inlineTextLink/InlineTextLink';
import LoadingBackdrop from '@/components/loadingBackdrop/LoadingBackdrop';
import { RichText } from '@/components/richText/RichText';
import SearchResultItem, { type SearchResultItemProps } from '@/components/searchResultItem/SearchResultItem';
import { SvgIcon } from '@/components/svgIcon';
import { TextField } from '@/components/textField';
import type { SearchSuggestion } from '@/helper/microservices/searchService';
import { getSearchTermCharLimitFieldProps, isSearchTermOverCharLimit } from '@/helper/search/searchTermCharLimit';
import { linkToString } from '@/helper/urls';
import Lupe from '@/public/icons/lupe.svg';
import CloseIcon from '@/public/icons/schliessen.svg';
import type { GlobalHelpCenterSettingsStoryblok } from '@/types/component-types-sb';
import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './HelpSearch.module.scss';
import { helpSearch, helpSuggest } from './HelpSearchHandler';

export type HelpSearchProps = {
  settings: GlobalHelpCenterSettingsStoryblok;
  isStartPage?: boolean;
};

const HelpSearch: React.FC<HelpSearchProps> = ({ settings, isStartPage = false }) => {
  const { t } = useTranslation();

  const searchSuggestions = settings.helpSearchSuggestions;
  const maxSearchResults = Number(settings.maxSearchResults ?? 5);

  const [searchResultLinks, setSearchResultLinks] = useState<SearchResultItemProps[]>([]);
  const [noSearchResults, setNoSearchResults] = useState(false);
  const [suggestions, setSuggestions] = useState<SearchSuggestion[] | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [showOpenAnnouncement, setShowOpenAnnouncement] = useState(false);
  const [showCloseAnnouncement, setShowCloseAnnouncement] = useState(false);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const searchBoxWrapperRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const firstNewResultRef = useRef<HTMLAnchorElement>(null);
  const [firstNewResultIndex, setFirstNewResultIndex] = useState<number | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const searchTermCharLimitFieldProps = getSearchTermCharLimitFieldProps(t, searchTerm);

  // Refs for focus trapping
  const searchFieldRef = useRef<HTMLInputElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const noResultsRef = useRef<HTMLDivElement>(null);

  const dialogTitleId = 'search-dialog-title';

  const fetchSearchResults = useCallback(
    async (page: number = 0, shouldFocusFirstNewResult: boolean = false) => {
      try {
        const searchResult = await helpSearch(searchTerm, page);

        window.dataLayer.push({
          event: 'generic',
          event_name: 'view_search_results',
          view_search_results: {
            search_term: `${searchTerm}`,
          },
        });

        if (page === 0) {
          // Reset results when starting a new search
          setSearchResultLinks(searchResult.results);
          setFirstNewResultIndex(null);
        } else {
          // Store the current length before appending new results
          const currentLength = searchResultLinks.length;

          // Append results when loading more
          setSearchResultLinks((prev) => [...prev, ...searchResult.results]);

          // Set the index of the first new result
          setFirstNewResultIndex(currentLength);

          // Focus on the first new result after the component has updated
          if (shouldFocusFirstNewResult && searchResult.results.length > 0) {
            // Use setTimeout to ensure the DOM has been updated
            setTimeout(() => {
              if (firstNewResultRef.current) {
                firstNewResultRef.current.focus({ preventScroll: true });
              }
            }, 100);
          }
        }
        setCurrentPage(searchResult.currentPage);
        setTotalPages(searchResult.pages);
        setIsLoadingResults(false);
        setHasSearched(true);
      } catch (error) {
        console.error('Error when retrieving the results:', error);
        setIsLoadingResults(false);
        setHasSearched(true);
      }
    },
    [searchTerm, searchResultLinks, firstNewResultRef]
  );

  useEffect(() => {
    if (isLoadingResults) {
      setNoSearchResults(false);
    } else {
      // Only consider it "no results" if we've actually performed a search
      // and the search term is at least 3 characters long
      const hasNoResults = !!searchTerm && searchResultLinks.length === 0 && hasSearched && searchTerm.length >= 3;
      setNoSearchResults(hasNoResults);
    }
  }, [isLoadingResults, searchResultLinks.length, searchTerm, isOpen, hasSearched]);

  useEffect(() => {
    if (isSearchTermOverCharLimit(searchTerm)) return;
    // Define an asynchronous function that executes the search process

    // Reset hasSearched when search term changes
    setHasSearched(false);

    const fetchSuggestionResults = async () => {
      try {
        const results = await helpSuggest(searchTerm);

        if (results.length <= 1 && searchTerm.toLocaleLowerCase() === results.at(0)?.word.toLocaleLowerCase()) {
          return;
        }
        setSuggestions(results ?? []);
      } catch (error) {
        console.error('Error fetching helpcenter suggestions.', error);
      }
    };

    // Call the asynchronous function directly within useEffect
    // The debounce delay is set here
    const debounceDelay = 300;

    const handler = setTimeout(() => {
      // Only perform search if search term is at least 3 characters
      if (searchTerm.length >= 3) {
        fetchSuggestionResults();
        setIsLoadingResults(true);
        // Reset to page 0 when search term changes
        setCurrentPage(0);
        fetchSearchResults(0);
      } else if (searchTerm.length === 0) {
        // Clear results when search term is empty
        setSearchResultLinks([]);
        setNoSearchResults(false);
      }
    }, debounceDelay);

    // Cleanup function: Timer is deleted if the searchTerm changes before the timeout has expired
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchSearchResults(nextPage, true);
  };

  function shouldDisplayTeaser(termsList: string) {
    return (
      !!termsList &&
      termsList.length > 0 &&
      searchTerm.length > 0 &&
      termsList.toLowerCase().split(',').includes(searchTerm.toLocaleLowerCase())
    );
  }

  const openOverlay = () => {
    if (isSearchTermOverCharLimit(searchTerm)) return;

    // a) queue live-region text (optional)
    setShowOpenAnnouncement(true);

    // b) render the dialog
    setIsOpen(true);

    // c) clear the message after a second
    setTimeout(() => setShowOpenAnnouncement(false), 1000);
  };

  const closeOverlay = useCallback(() => {
    // a) queue live-region text
    setShowCloseAnnouncement(true);

    // b) close the dialog
    setIsOpen(false);

    // c) clear the message after a second
    setTimeout(() => setShowCloseAnnouncement(false), 1000);
  }, [setShowCloseAnnouncement, setIsOpen]);

  // Handle keyboard navigation for focus trapping
  const handleTabKey = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen || !searchBoxRef.current) return;

      // Get all focusable elements in the search box
      const focusableElements = searchBoxRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      // If shift + tab and focus is on first element, move to last element
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus({ preventScroll: true });
      }
      // If tab and focus is on last element, move to first element
      else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus({ preventScroll: true });
      }
    },
    [isOpen]
  );

  // Handle escape key to close the overlay
  const handleEscapeKey = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        closeOverlay();
      }
    },
    [isOpen, closeOverlay]
  );

  // Add event listeners for keyboard navigation
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleTabKey);
      document.addEventListener('keydown', handleEscapeKey);
    } else {
      document.removeEventListener('keydown', handleTabKey);
      document.removeEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleTabKey);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, handleTabKey, handleEscapeKey]);

  // Lock body scroll when search box is open
  useEffect(() => {
    const searchBox = searchBoxWrapperRef?.current;

    // When search box is open, make it fill the remaining viewport height from its current position to the bottom.
    const updateHeight = () => {
      if (!searchBox) return;

      const height = window.innerHeight - searchBox.getBoundingClientRect().top;

      searchBox.style.minHeight = `${height}px`;
      searchBox.style.height = `${height}px`;
      searchBox.style.maxHeight = `${height}px`;
    };

    if (isOpen) {
      window.scrollTo({ top: 0 });
      // lock body scroll
      document.body.style.overflow = 'hidden';

      // reserve gutter on html, using setProperty so TS doesn't complain
      document.documentElement.style.setProperty('scrollbar-gutter', 'stable');

      /*  // Focus on search field when opening
        setTimeout(() => {
          if (searchFieldRef.current) {
            searchFieldRef.current.focus({ preventScroll: true });
          }
        }, 500);*/

      // Show announcement for screen readers
      setShowOpenAnnouncement(true);
      // Hide announcement after a short delay
      setTimeout(() => {
        setShowOpenAnnouncement(false);
      }, 1000);

      updateHeight();
      window.addEventListener('resize', updateHeight);
    } else {
      // restore body
      document.body.style.overflow = '';

      // remove the gutter reservation
      document.documentElement.style.removeProperty('scrollbar-gutter');

      // remove the open-styles of the search box
      searchBox?.style.removeProperty('min-height');
      searchBox?.style.removeProperty('height');
      searchBox?.style.removeProperty('max-height');
    }

    return () => {
      window.removeEventListener('resize', updateHeight);
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <div
      ref={searchBoxWrapperRef}
      className={classNames(classes.searchBoxWrapper, {
        [classes.open]: isOpen,
      })}
    >
      <div
        ref={searchBoxRef}
        id="search-dialog"
        className={classNames(classes.searchBox, { [classes.open]: isOpen })}
        role="dialog"
        aria-modal={isOpen}
        aria-labelledby={dialogTitleId}
        tabIndex={-1} // 1️⃣ focusable
      >
        <div className={classes.visuallyHidden} aria-live="assertive">
          {showOpenAnnouncement ? t('Suche geöffnet') : showCloseAnnouncement ? t('Suche geschlossen') : ''}
        </div>
        <div role="search">
          {isOpen && (
            <IconButton
              data-testid="close-search-button"
              ref={closeButtonRef}
              className={classes.closeButton}
              onClick={() => closeOverlay()}
              aria-label={t('Suche schließen')}
            >
              <CloseIcon aria-hidden="true" />
            </IconButton>
          )}

          <div className={classes.contentGrid}>
            <div className={classes.content}>
              <div>
                <HeadlineField className={classNames(classes.subtitle)} tag={HeadlineTag.H1} style={HeadlineTag.H6}>
                  {settings.helpSearchSubTitle}
                </HeadlineField>
                <HeadlineField
                  className={classes.title}
                  tag={isOpen ? HeadlineTag.H6 : HeadlineTag.H2}
                  style={isOpen ? HeadlineTag.H6 : HeadlineTag.H3}
                >
                  {settings.helpSearchTitle}
                </HeadlineField>
                <TextField
                  inputRef={searchFieldRef}
                  error={searchTermCharLimitFieldProps.error}
                  helperText={searchTermCharLimitFieldProps.helperText}
                  value={searchTerm}
                  onChange={onChangeHandler}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      openOverlay();
                    }
                  }}
                  placeholder={t('helpCenter.searchPlaceholder')}
                  aria-label={t('helpCenter.searchPlaceholder')}
                  slotProps={{
                    htmlInput: {
                      'data-testid': 'search-input',
                    },
                    input: {
                      endAdornment: (
                        <IconButton
                          data-testid="start-search-button"
                          className={classNames('secondary', classes.searchButton)}
                          size="small"
                          onClick={openOverlay}
                          aria-label={t('Suche starten')}
                          aria-haspopup="dialog"
                          aria-controls="search-dialog"
                          aria-expanded={isOpen}
                        >
                          <SvgIcon component={Lupe} />
                        </IconButton>
                      ),
                    },
                  }}
                  fullWidth
                />

                <div className={classes.linkContainer}>
                  {searchSuggestions?.map((suggestion, i) => (
                    <InlineTextLink
                      className={classes.linkItem}
                      data-testid="search-suggestion-link"
                      key={i}
                      tabIndex={0}
                      onClick={() => {
                        setSearchTerm(suggestion.searchSuggestion);
                        setIsOpen(true);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSearchTerm(suggestion.searchSuggestion);
                          setIsOpen(true);
                        }
                      }}
                    >
                      {suggestion.searchSuggestion}
                    </InlineTextLink>
                  ))}
                </div>
              </div>
              {isOpen && (
                <div data-testid="help-search-results-container" className={classes.searchResultContainer}>
                  {settings.helpSearchTeaserList
                    ?.filter((teaser) => shouldDisplayTeaser(teaser.searchterms ?? ''))
                    .map((teaser) => (
                      <HighlightCardComponent
                        key={teaser._uid}
                        variant="green"
                        subtitle={teaser.category ?? ''}
                        title={teaser.headline ?? ''}
                        link={linkToString(teaser.link)}
                        label={teaser.linkText}
                      />
                    ))}
                  <LoadingBackdrop type="card" loading={isLoadingResults} />
                  {!isLoadingResults && (
                    <div>
                      {noSearchResults ? (
                        <div ref={noResultsRef} tabIndex={0} aria-live="polite">
                          <RichText text={settings.helpSearchNoResultsInfoText} />
                        </div>
                      ) : (
                        <>
                          {searchResultLinks.map((result, i) => (
                            <SearchResultItem
                              key={i}
                              searchTerm={searchTerm}
                              subtitle={result.subtitle}
                              title={result.title}
                              link={result.link}
                              ref={firstNewResultIndex === i ? firstNewResultRef : undefined}
                            />
                          ))}
                          {currentPage < totalPages - 1 && searchResultLinks.length > 0 && (
                            <div className={classes.loadMoreContainer}>
                              <Button
                                data-testid="search-result-item-load-more-button"
                                variant="ctaFull"
                                onClick={() => handleLoadMore()}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleLoadMore();
                                  }
                                }}
                                tabIndex={0}
                                aria-label={t('Mehr laden')}
                              >
                                {t('Mehr laden')}
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpSearch;
